import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Users whose entries never render in any bitácora view. Display-only: their
// actions ARE still written to the collection (audit trail intact) — they are
// filtered out at read time in BitacoraTimeline and from the Bitácora user
// dropdown.
export const BITACORA_HIDDEN_USER_IDS = new Set([
  'jsFGCm73XPQErnwxoP69Sy9TdsC2',
]);

/**
 * Recursively remove fields with `undefined` values — Firestore rejects them.
 * Preserves null, primitives, arrays, and plain objects.
 */
function stripUndefined(value) {
  if (value === undefined) return null;
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value
      .map((v) => (v === undefined ? null : stripUndefined(v)));
  }
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (v === undefined) continue;
    out[k] = stripUndefined(v);
  }
  return out;
}

/**
 * Log an action to the bitacora collection
 * @param {Object} params - Logging parameters
 * @param {string} params.action - CREATE | UPDATE | DELETE
 * @param {string} params.collection - Collection name (clientes | contracts | links | links_files)
 * @param {string} params.documentId - Document ID that was modified
 * @param {string} [params.subCollection] - Optional subcollection name
 * @param {string} [params.subDocumentId] - Optional subdocument ID
 * @param {Array} [params.changes] - Array of {field, oldValue, newValue} objects
 * @param {Object} params.metadata - Additional context
 * @param {string} [params.metadata.clientId] - Client ID for reference
 * @param {string} [params.metadata.clientName] - Client name for display
 * @param {string} [params.metadata.contratoId] - Contract ID if applicable
 * @param {string} [params.metadata.linkId] - Link ID if applicable
 * @param {string} params.metadata.description - Executive-level description
 * @param {Object} params.user - User object from UserContext
 * @param {string} params.user.uid - User ID
 * @param {Object} params.userData - User data from UserContext
 * @returns {Promise<void>}
 */
export async function logBitacora({
  action,
  collection: collectionName,
  documentId,
  subCollection,
  subDocumentId,
  changes,
  metadata,
  user,
  userData,
}) {
  try {
    // Validate required parameters
    if (!action || !collectionName || !documentId || !metadata?.description) {
      console.error('Missing required parameters for bitacora logging', {
        action,
        collectionName,
        documentId,
        metadata,
      });
      return;
    }

    if (!user?.uid || !userData) {
      console.error('User information not available for bitacora logging');
      return;
    }

    // Build log entry
    const logEntry = {
      timestamp: Date.now(),
      userId: user.uid,
      userName: userData.nombre || 'Unknown',
      userEmail: userData.correo || 'unknown@example.com',
      userRole: userData.rol || 'unknown',
      orgId: userData.org || 'unknown',
      action,
      collection: collectionName,
      documentId,
      metadata: {
        ...metadata,
        description: metadata.description,
      },
    };

    // Add optional fields if present
    if (subCollection) {
      logEntry.subCollection = subCollection;
    }
    if (subDocumentId) {
      logEntry.subDocumentId = subDocumentId;
    }
    if (changes && changes.length > 0) {
      logEntry.changes = changes;
    }

    // Write to Firestore — strip undefined fields recursively (Firestore rejects them)
    await addDoc(collection(db, 'bitacora'), stripUndefined(logEntry));

    console.log('Bitacora entry logged:', logEntry.metadata.description);
  } catch (error) {
    console.error('Error logging to bitacora:', error);
    // Don't throw - logging should not break the main operation
  }
}

/**
 * Helper to compute changes between old and new objects
 * Only includes fields that actually changed
 * @param {Object} oldData - Previous state
 * @param {Object} newData - New state
 * @param {Array<string>} fieldsToTrack - List of field paths to track
 * @returns {Array} Array of {field, oldValue, newValue} objects
 */
export function computeChanges(oldData, newData, fieldsToTrack) {
  const changes = [];

  fieldsToTrack.forEach((fieldPath) => {
    const oldValue = getNestedValue(oldData, fieldPath);
    const newValue = getNestedValue(newData, fieldPath);

    // Only log if values are different
    if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
      changes.push({
        field: fieldPath,
        oldValue,
        newValue,
      });
    }
  });

  return changes;
}

/**
 * Get nested value from object using dot notation
 * @param {Object} obj - Object to traverse
 * @param {string} path - Dot-separated path (e.g., "fianzasRequeridas.value")
 * @returns {any} Value at path or undefined
 */
function getNestedValue(obj, path) {
  if (!obj) return undefined;

  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = current[key];
  }

  return current;
}

/**
 * Generate human-readable description for common operations
 * @param {string} collection - Collection name
 * @param {string} action - Action type
 * @param {Object} context - Additional context
 * @returns {string} Description
 */
export function generateDescription(collection, action, context = {}) {
  const { clientName, fieldName, newValue, itemName } = context;

  // Clientes
  if (collection === 'clientes') {
    if (action === 'CREATE') {
      return `Creó nuevo cliente '${clientName}'`;
    }
    if (action === 'UPDATE') {
      if (fieldName === 'status') {
        const statusMap = {
          green: 'verde',
          orange: 'naranja',
          red: 'rojo',
          gray: 'gris',
        };
        return `Actualizó estado del cliente a '${statusMap[newValue] || newValue}'`;
      }
      if (fieldName === 'statusDescription') {
        return `Actualizó descripción del estado del cliente`;
      }
      if (fieldName === 'notes') {
        return `Actualizó notas del cliente`;
      }
      return `Actualizó información del cliente`;
    }
  }

  // Contracts
  if (collection === 'contracts') {
    if (action === 'UPDATE') {
      if (fieldName === 'fianzasRequeridas.value') {
        return itemName ? `Agregó fianza requerida '${itemName}'` : 'Modificó fianzas requeridas';
      }
      if (fieldName === 'fianzas') {
        return itemName ? `Subió nueva fianza emitida #${itemName}` : 'Modificó fianzas emitidas';
      }
      if (fieldName === 'fianzas.pagada') {
        return `Marcó fianza como ${newValue ? 'pagada' : 'pendiente de pago'}`;
      }
      return 'Actualizó información del contrato';
    }
  }

  // Links
  if (collection === 'links') {
    if (action === 'CREATE') {
      return `Creó enlace de expediente '${itemName}'`;
    }
    if (action === 'UPDATE' && fieldName === 'files.required') {
      return `${newValue ? 'Marcó' : 'Desmarcó'} archivo como requerido`;
    }
  }

  if (collection === 'links_files') {
    if (action === 'UPDATE' && fieldName === 'status') {
      return `Archivo procesado: ${newValue}`;
    }
  }

  // Convenios
  if (collection === 'convenios') {
    if (action === 'CREATE') {
      return `Creó nuevo convenio '${clientName || itemName || ''}'`;
    }
    if (action === 'UPDATE') {
      if (fieldName === 'beneficiarios') {
        return itemName ? `Agregó beneficiario '${itemName}'` : 'Modificó beneficiarios';
      }
      if (fieldName === 'proyectos') {
        return itemName ? `Agregó proyecto '${itemName}'` : 'Modificó proyectos';
      }
      return 'Actualizó información del convenio';
    }
    if (action === 'DELETE') {
      return `Eliminó convenio '${clientName || itemName || ''}'`;
    }
  }

  // Default
  return `${action} en ${collection}`;
}

/**
 * Compute user assignment changes and generate description
 * @param {Array} oldUsers - Array of previous user UIDs
 * @param {Array} newUsers - Array of new user UIDs
 * @param {Function} getUserName - Function to resolve UID to name
 * @returns {Object} { added, removed, description, changes, hasChanges }
 */
export function computeUserAssignmentChanges(oldUsers = [], newUsers = [], getUserName) {
  const oldSet = new Set(oldUsers);
  const newSet = new Set(newUsers);

  const added = newUsers.filter(uid => !oldSet.has(uid));
  const removed = oldUsers.filter(uid => !newSet.has(uid));

  // Generate human-readable descriptions
  const parts = [];
  if (added.length > 0) {
    const names = added.map(uid => getUserName(uid) || uid);
    parts.push(`Asignó a ${names.join(', ')}`);
  }
  if (removed.length > 0) {
    const names = removed.map(uid => getUserName(uid) || uid);
    parts.push(`Desasignó a ${names.join(', ')}`);
  }

  return {
    added,
    removed,
    description: parts.join('. ') || 'Sin cambios',
    changes: (added.length || removed.length) ? [{ field: 'assignedUsers', oldValue: oldUsers, newValue: newUsers }] : [],
    hasChanges: added.length > 0 || removed.length > 0,
  };
}
